import React, { Fragment, useEffect, useMemo, useState } from 'react'
import { Highlight, Snippet, useInfiniteHits, useRefinementList, useSearchBox } from 'react-instantsearch'
import { router } from '@inertiajs/react'
import { useLocalStorage } from 'usehooks-ts'

import { useSearch } from '@/components/search-provider'
import CtaLink, { buttonStyles } from '@/components/ui/cta-link'
import Link from '@/components/link-component'
import HitPreview from './hit-preview'
import IconFont from '@/components/ui/icon-font'
import DblAngleRightIcon from '@/icons/bwi/dbl-angle-right.svg'
import AiIcon from '@/icons/ai-icon.svg'
import { ICONS } from './icons'

import { interpolate, localizedPath, removeLocalePrefix, useLocale, useTranslations } from '@/utils/locale'
import { capitalize, cx } from '@/utils/strings'
import { HitProps } from './types'
import { useLocation } from '@/utils/url'
import Routes from '@/constants/routes'

type InfiniteHitsProps = {
  className?: string
  variant?: 'list' | 'preview'
  /** Group results by type. Disables load more if enabled */
  shouldGroupResults?: boolean
  showIcons?: boolean
  onLinkClick?: (hit: HitProps, event: React.MouseEvent<HTMLAnchorElement>) => void
  onLinkPreview?: (hit: HitProps | null) => void
  showRefinements?: boolean
}

export default function InfiniteHits({
  className,
  variant = 'list',
  shouldGroupResults = false,
  showIcons = true,
  onLinkClick,
  onLinkPreview: onLinkSelect,
  showRefinements = false,
}: InfiniteHitsProps) {
  const { isAskAiEnabled, setAiQuery, setIsChatOpen, setIsModalOpen } = useSearch()
  const { query } = useSearchBox()
  const { items, showMore, isLastPage, sendEvent, results } = useInfiniteHits<HitProps>()
  const { items: facetItems, refine } = useRefinementList({
    attribute: 'type',
    sortBy: ['name:asc'],
  })
  const { t } = useTranslations()
  const { path } = useLocation()
  const { currentLocale } = useLocale()
  const helpPath = localizedPath('/help/', currentLocale)
  const askAiPath = localizedPath('/ask-ai/', currentLocale)

  useEffect(() => {
    // Default refinement on help pages
    if (removeLocalePrefix(path).startsWith('/help/')) {
      refine('help')
    }
  }, [path])

  const [activeHit, setActiveHit] = useState<HitProps | null>(null)
  useEffect(() => {
    onLinkSelect?.(activeHit)
  }, [activeHit])

  const getHitElement = (hit: HitProps) =>
    document.querySelector<HTMLAnchorElement>(`[data-object-id="${hit?.objectID}"]`)

  // Set the first hit as active when the results change
  useEffect(() => {
    if (variant === 'preview') {
      setActiveHit(items?.[0] ?? null)
    }
  }, [results?.queryID])

  // Scroll the active hit into view
  useEffect(() => {
    if (activeHit && usedKeyboard) {
      const el = getHitElement(activeHit)
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const onFocus = (e: FocusEvent) => {
        e.preventDefault()
      }
      el?.addEventListener('focus', onFocus)
      el?.focus()

      return () => {
        el?.removeEventListener('focus', onFocus)
      }
    }
  }, [activeHit])

  // Keeping track if the user used the keyboard to prevent conflicts
  const [usedKeyboard, setUsedKeyboard] = useState(false)
  // Add keyboard shortcuts
  useEffect(() => {
    const moveActiveHit = (direction: 'up' | 'down') => {
      const index = items.findIndex((hit) => hit.objectID === activeHit?.objectID)
      const nextItem = items[direction === 'down' ? index + 1 : index - 1]
      if (nextItem) {
        setActiveHit(nextItem)
      }
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'ArrowDown') {
        moveActiveHit('down')
        setUsedKeyboard(true)
      }
      if (event.key === 'ArrowUp') {
        moveActiveHit('up')
        setUsedKeyboard(true)
      }
      if (event.key === 'Enter' && activeHit) {
        const el = getHitElement(activeHit)
        el?.click()
      }
    }
    const onMouseMove = () => {
      setUsedKeyboard(false)
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('mousemove', onMouseMove)
    return (): void => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('mousemove', onMouseMove)
    }
  }, [activeHit, items])

  const askAiSuggestion = useMemo(() => {
    if (!isAskAiEnabled) return null
    return (
      <button
        className={cx(
          'text-left flex items-center rounded-lg p-4 mb-4 cursor-pointer select-none',
          'border border-black/10 dark:border-white/10',
          'hover:bg-secondary-50 hover:dark:bg-black/10 transition-colors'
        )}
        type="button"
        title={t('Search for {query} in Ask AI').replace('{query}', query)}
        onClick={(e) => {
          e.preventDefault()
          if (path.startsWith(helpPath)) {
            setIsChatOpen(true)
          } else {
            router.visit(askAiPath)
          }
          setAiQuery(query)
          setIsModalOpen(false)
          return false
        }}
        onAuxClick={(e) => {
          e.preventDefault()
          setAskAiQuery(query)
          window.open(`${Routes.hostname}${localizedPath('/ask-ai/', currentLocale)}`, '_blank', 'noreferrer,noopener')
          return false
        }}
      >
        <AiIcon className="size-4 md:size-6 mr-2 md:mr-4 md:ml-1 text-primaryBlue" />
        <div className="flex-1">
          <h4 className="mb-0 text-sm md:text-lg text-black dark:text-white pt-0 pb-0">
            {interpolate(t('Ask AI about {query}'), {
              query: <span className="text-primaryBlue">{query}</span>,
            })}
          </h4>
        </div>
      </button>
    )
  }, [isAskAiEnabled, query, helpPath, askAiPath])

  const [, setAskAiQuery] = useLocalStorage<string | undefined>('askAiQuery', undefined)

  return (
    query.length > 0 && (
      <div className={className}>
        {showRefinements && facetItems && facetItems.length > 0 && (
          <div className="px-1 md:px-4 py-2 md:py-5 border-b-2 border-secondary-50 dark:border-white/10">
            <div className="flex flex-wrap gap-2 pl-2">
              {facetItems.map((item) => {
                if (!item.label) return null
                return (
                  <button
                    key={item.value}
                    type="button"
                    className={cx(
                      'flex items-center justify-center px-3 py-1.5 rounded-full border dark:border-0 text-xs md:text-sm font-medium transition-colors',
                      item.isRefined
                        ? 'bg-primaryBlue dark:bg-white text-white dark:text-primary-500 border-primaryBlue dark:border-white'
                        : 'bg-white dark:bg-white/10 text-primary-700 hover:bg-secondary-50/40 dark:text-white dark:hover:bg-white/25'
                    )}
                    onClick={() => {
                      refine(item.value)
                    }}
                  >
                    <span>{capitalize(item.label)}</span>
                    <span
                      className={cx(
                        'ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-xs font-bold',
                        item.isRefined
                          ? 'bg-white dark:bg-primary-500 text-primaryBlue dark:text-white'
                          : 'bg-secondary-100 dark:bg-secondary-50 text-secondary-600'
                      )}
                    >
                      {item.count}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        )}
        {!(items.length > 0) ? (
          <div className="mt-5 px-5">
            {isAskAiEnabled && <div className="mb-5">{askAiSuggestion}</div>}
            <div>
              0 results for &ldquo;
              <strong>{query}</strong>&rdquo;{' '}
            </div>
          </div>
        ) : (
          <div className={cx(variant === 'preview' && 'relative flex h-full')}>
            <div
              className={cx(
                variant === 'list' && 'flex flex-col gap-2',
                variant === 'preview' &&
                  'flex flex-col gap-2 w-full md:w-1/2 max-h-full overflow-auto p-3 md:p-5 pb-10 border-r border-secondary-50 dark:border-white/10 scrollbar-thumb-secondary-200'
              )}
              onKeyDown={(e) => {
                // Prevent scrolling with arrow keys because we scroll the active item manually
                if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault()
                }
              }}
            >
              {askAiSuggestion}
              {items.map((hit, index) => {
                if (!hit || !hit.url) return null
                const isHighlighted = hit.objectID === activeHit?.objectID
                const group = hit.category
                const prevGroup = index > 0 ? items[index - 1].category : null
                const prevGroupIsDifferent = index > 0 ? prevGroup !== group : false
                return (
                  <Fragment key={hit.objectID}>
                    {shouldGroupResults && (index === 0 || prevGroupIsDifferent) && (
                      <div
                        className={cx(
                          'text-xs md:text-sm text-primaryBlue font-semibold uppercase mb-2',
                          index > 0 && 'mt-4'
                        )}
                      >
                        {group}
                      </div>
                    )}
                    <Link
                      data-object-id={hit.objectID}
                      to={`${hit.url}${hit.hash ? `#${hit.hash}` : ''}`}
                      className={cx(
                        'rounded-sm px-4 pt-2 pb-3 block',
                        variant === 'list' && 'hover:bg-secondary-50 dark:hover:bg-secondary-700/80 hover:shadow-sm',
                        isHighlighted && variant === 'preview' && 'bg-secondary-50 dark:bg-secondary-700'
                      )}
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        sendEvent('click', hit, 'Result Clicked')
                        sendEvent('conversion', hit, 'Result Converted')
                        onLinkClick?.(hit, e)
                      }}
                      onFocus={(e: FocusEvent) => {
                        e.preventDefault()
                        setUsedKeyboard(true)
                        setActiveHit(hit)
                      }}
                    >
                      <div
                        className="overflow-hidden"
                        onMouseEnter={() => {
                          if (variant === 'preview') {
                            if (!usedKeyboard) setActiveHit(hit)
                            const activeElement = document.activeElement as HTMLAnchorElement
                            if (activeElement?.dataset?.objectId) {
                              activeElement?.blur()
                            }
                          }
                        }}
                      >
                        {variant === 'list' && (
                          <div className="text-eyebrow uppercase text-secondary-500 dark:text-secondary-300 mb-1">
                            <span className="text-sm">
                              {showIcons && hit.type && (
                                <IconFont className="text-xl leading-4 p-1" unicode={ICONS[hit.type] || 'e96e'} />
                              )}
                            </span>
                            {hit.type}
                          </div>
                        )}
                        {variant === 'preview' && (
                          <div className="mb-1">
                            {hit.breadcrumbs?.slice(1).map((breadcrumb, index) => (
                              <div
                                key={index}
                                className="inline-flex text-xs md:text-sm text-secondary-500 dark:text-secondary-200 items-center justify-center whitespace-pre"
                              >
                                {index > 0 && <DblAngleRightIcon className="w-2 h-2 mx-1" />}
                                {breadcrumb}
                              </div>
                            ))}
                          </div>
                        )}
                        <h4
                          className={cx(
                            'mb-0 text-base md:text-lg py-1 text-secondary-900 dark:text-white font-medium'
                          )}
                        >
                          <Highlight
                            hit={hit}
                            attribute="title"
                            classNames={{
                              highlighted: cx('dark:text-secondary-900'),
                            }}
                          />
                        </h4>
                        {variant === 'list' && (
                          <p className={cx('text-sm mb-0 text-secondary-500 dark:text-secondary-300')}>
                            <Snippet
                              hit={hit}
                              attribute="body"
                              classNames={{
                                highlighted: cx('dark:text-secondary-900'),
                              }}
                            />
                          </p>
                        )}
                      </div>
                    </Link>
                  </Fragment>
                )
              })}
              {!isLastPage && !shouldGroupResults && (
                <div className="w-full text-center pt-8">
                  <CtaLink
                    asButton
                    className="rounded-full px-6 py-2"
                    style={buttonStyles.btnOutlined}
                    onClick={showMore}
                  >
                    Show more results
                  </CtaLink>
                </div>
              )}
            </div>
            {variant === 'preview' && activeHit && <HitPreview hit={activeHit} onLinkClick={onLinkClick} />}
          </div>
        )}
      </div>
    )
  )
}
